import { useEffect, useState, type FormEvent } from "react";
import { AlertTriangle, Archive, ArchiveRestore, Check, Mail, RefreshCcw } from "lucide-react";
import { api, ApiError } from "../../api";
import { Button, Card, Field } from "../../ui";

type Building = { id: string; name: string };
type Unit = { id: string; unitNumber: string };

type PendingInvite = {
  id: string;
  buildingId: string;
  buildingName: string;
  roleCode: string;
  unitId: string | null;
  unitNumber: string | null;
  invitedEmail: string;
  issuedAt: string;
  expiresAt: string;
  redeemedAt: string | null;
  revokedAt: string | null;
  archivedAt: string | null;
};

/** Per-send delivery telemetry returned by the issue / resend
 *  endpoints. Drives the inline "Email delivered" / "Email
 *  stubbed (no API key)" / "Email failed" copy on the recently-
 *  issued list. */
type EmailDelivery =
  | { status: "sent"; providerId: string | null }
  | { status: "stub"; reason: string }
  | { status: "failed"; error: string };

/** Server-side configuration snapshot. Lets the admin see at a
 *  glance whether outbound email is even configured before
 *  issuing an invite and waiting on a delivery that never
 *  happens. */
type EmailStatus = {
  resendConfigured: boolean;
  emailFrom: string;
  appUrl: string;
};

const ROLES = [
  { code: "unit_owner", label: "Unit Owner" },
  { code: "board_member", label: "Board Member" },
  { code: "property_manager", label: "Property Manager" },
  { code: "chief_engineer", label: "Chief Engineer" },
  { code: "admin", label: "Administrator" },
] as const;

export function AdminInvites() {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [buildingId, setBuildingId] = useState("");
  const [units, setUnits] = useState<Unit[]>([]);
  const [form, setForm] = useState({
    invitedEmail: "",
    roleCode: "unit_owner" as (typeof ROLES)[number]["code"],
    unitId: "",
  });
  const [issued, setIssued] = useState<
    {
      email: string;
      role: string;
      code: string;
      expiresAt: string;
      emailDelivery: EmailDelivery;
    }[]
  >([]);
  const [pending, setPending] = useState<PendingInvite[] | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [emailStatus, setEmailStatus] = useState<EmailStatus | null>(null);

  const reloadPending = async (withArchived = includeArchived) => {
    try {
      const r = await api.get<{ data: PendingInvite[] }>(
        withArchived
          ? "/api/admin/invites?includeArchived=1"
          : "/api/admin/invites",
      );
      setPending(r.data);
    } catch (err) {
      setError(
        err instanceof ApiError ? JSON.stringify(err.body) : String(err),
      );
    }
  };

  useEffect(() => {
    void reloadPending(includeArchived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived]);

  useEffect(() => {
    void api.get<{ data: Building[] }>("/api/buildings").then((r) => {
      setBuildings(r.data);
      if (r.data[0]) setBuildingId(r.data[0].id);
    });
    void reloadPending();
    // Pull the email status diagnostic once on mount so the
    // banner can warn the admin if RESEND_API_KEY isn't
    // configured before they spend a minute filling out the
    // invite form. Failure to load is silent — the banner just
    // doesn't render.
    void api
      .get<{ data: EmailStatus }>("/api/admin/email-status")
      .then((r) => setEmailStatus(r.data))
      .catch(() => setEmailStatus(null));
  }, []);

  useEffect(() => {
    if (!buildingId) return;
    void api
      .get<{ data: Unit[] }>(`/api/buildings/${buildingId}/units`)
      .then((r) => setUnits(r.data));
  }, [buildingId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.post<{
        inviteCode: string;
        expiresAt: string;
        emailDelivery: EmailDelivery;
      }>("/api/admin/invites", {
        buildingId,
        roleCode: form.roleCode,
        invitedEmail: form.invitedEmail,
        unitId: form.unitId || undefined,
      });
      setIssued([
        {
          email: form.invitedEmail,
          role: form.roleCode,
          code: res.inviteCode,
          expiresAt: res.expiresAt,
          emailDelivery: res.emailDelivery,
        },
        ...issued,
      ]);
      setForm({ ...form, invitedEmail: "", unitId: "" });
      await reloadPending();
    } catch (err) {
      setError(err instanceof ApiError ? JSON.stringify(err.body) : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {emailStatus && !emailStatus.resendConfigured && (
        <Card className="!bg-status-review-bg !border-status-review-fg/20">
          <div className="text-[12.5px] text-status-review-fg leading-snug inline-flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              <strong>Outbound email is not configured.</strong>{" "}
              <code>RESEND_API_KEY</code> is unset, so the portal logs invite
              emails to the server console instead of sending them. Issue
              invites here as usual; copy the code from the table below and
              share it with the invitee out-of-band until email is wired up.
            </span>
          </div>
        </Card>
      )}

      <Card>
        <h2 className="text-ink-800 m-0">Issue invite code</h2>
        <p className="text-xs text-ink-500 mt-1 mb-4">
          Codes are single-use, hashed at rest, and expire in 30 days. Send the
          code to the invitee out-of-band; they redeem at /register.
        </p>
        <form onSubmit={submit}>
          <div className="field-row">
            <Field label="Building">
              <select
                className="select"
                value={buildingId}
                onChange={(e) => setBuildingId(e.target.value)}
              >
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Role">
              <select
                className="select"
                value={form.roleCode}
                onChange={(e) =>
                  setForm({
                    ...form,
                    roleCode: e.target.value as (typeof ROLES)[number]["code"],
                  })
                }
              >
                {ROLES.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </select>
            </Field>
            {form.roleCode === "unit_owner" && (
              <Field label="Unit">
                <select
                  className="select"
                  value={form.unitId}
                  onChange={(e) => setForm({ ...form, unitId: e.target.value })}
                  required
                >
                  <option value="">Choose…</option>
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      Unit {u.unitNumber}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </div>
          <Field label="Email">
            <input
              className="input"
              type="email"
              value={form.invitedEmail}
              onChange={(e) =>
                setForm({ ...form, invitedEmail: e.target.value })
              }
              required
            />
          </Field>
          {error && <div className="alert-error">{error}</div>}
          <Button variant="primary" type="submit" loading={busy}>
            <Mail size={16} />
            {busy ? "Issuing…" : "Issue invite"}
          </Button>
        </form>
      </Card>

      {issued.length > 0 && (
        <Card>
          <h3 className="text-ink-800 m-0 mb-2">Recently issued</h3>
          <p className="text-xs text-ink-500 mb-3">
            The portal shows the code only once. Copy it now and send it.
            The Email column shows whether the system also delivered the
            invite by email.
          </p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Code</th>
                  <th>Expires</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {issued.map((i, idx) => (
                  <tr key={idx}>
                    <td className="font-medium text-ink-800">{i.email}</td>
                    <td>
                      <code>{i.role}</code>
                    </td>
                    <td>
                      <code className="text-xs select-all">{i.code}</code>
                    </td>
                    <td className="text-xs text-ink-500">
                      {new Date(i.expiresAt).toLocaleDateString()}
                    </td>
                    <td>
                      <EmailDeliveryBadge delivery={i.emailDelivery} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <PendingInvitesCard
        pending={pending}
        includeArchived={includeArchived}
        onToggleIncludeArchived={setIncludeArchived}
        onChange={() => reloadPending()}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending invites list with a per-row Resend action.
//
// Resend always rotates: codes are stored as SHA-256, so the
// original plaintext can't be recovered. The server marks the old
// row revoked, issues a fresh code, and emails it. The list
// reloads after a successful resend so the new row appears with
// the updated issuedAt.
// ---------------------------------------------------------------------------
function PendingInvitesCard({
  pending,
  includeArchived,
  onToggleIncludeArchived,
  onChange,
}: {
  pending: PendingInvite[] | null;
  /** When true, the listing fetch already includes archived rows
   *  and the toggle reads "Hide archived". */
  includeArchived: boolean;
  onToggleIncludeArchived: (next: boolean) => void;
  onChange: () => void | Promise<void>;
}) {
  // The toggle lives at the top of the card so it's discoverable
  // even when the list is empty (admin can flip it without
  // first issuing an invite). Header copy stays consistent
  // across loading / empty / non-empty states.
  const header = (
    <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
      <div>
        <h3 className="text-ink-800 m-0">Pending invites</h3>
        <p className="text-xs text-ink-500 mt-1 mb-0">
          Active codes show first. Resend rotates the code (the prior one
          is retired) and emails a fresh one. Cooldown is 5 minutes per
          invite. Archive dismisses an invite from this view without
          deleting it; the row stays in the audit trail.
        </p>
      </div>
      <label className="inline-flex items-center gap-1.5 text-[12px] text-ink-700 cursor-pointer whitespace-nowrap">
        <input
          type="checkbox"
          checked={includeArchived}
          onChange={(e) => onToggleIncludeArchived(e.target.checked)}
          className="rounded border-ink-300 text-brand-700 focus:ring-brand-500"
        />
        Show archived
      </label>
    </div>
  );

  if (pending === null) {
    return (
      <Card>
        {header}
        <p className="text-ink-500 text-sm m-0">Loading…</p>
      </Card>
    );
  }
  if (pending.length === 0) {
    return (
      <Card>
        {header}
        <p className="text-ink-500 text-sm m-0">
          {includeArchived
            ? "No invites in this view. Issue one above when someone needs access."
            : "No outstanding invites. Issue one above when someone needs access."}
        </p>
      </Card>
    );
  }

  // Show active first, then revoked, then archived. Within each
  // group, newest first.
  const sorted = [...pending].sort((a, b) => {
    const groupRank = (i: PendingInvite) => {
      if (i.archivedAt) return 2;
      if (i.revokedAt || new Date(i.expiresAt).getTime() <= Date.now())
        return 1;
      return 0;
    };
    const ra = groupRank(a);
    const rb = groupRank(b);
    if (ra !== rb) return ra - rb;
    return new Date(b.issuedAt).getTime() - new Date(a.issuedAt).getTime();
  });

  return (
    <Card>
      {header}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Building</th>
              <th>Role</th>
              <th>Issued</th>
              <th>Expires</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((inv) => (
              <PendingInviteRow
                key={inv.id}
                invite={inv}
                onChange={onChange}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PendingInviteRow({
  invite,
  onChange,
}: {
  invite: PendingInvite;
  onChange: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const expired = new Date(invite.expiresAt).getTime() < Date.now();
  const revoked = !!invite.revokedAt;
  const archived = !!invite.archivedAt;
  // Status badge prefers archived to other states because archive
  // is the most recent admin signal — even if the row is also
  // revoked or expired, "archived" is the meaningful label for
  // the admin scanning the list.
  const status = archived
    ? "Archived"
    : revoked
      ? "Revoked"
      : expired
        ? "Expired"
        : "Active";

  const archive = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(
        `/api/admin/invites/${invite.id}/${archived ? "unarchive" : "archive"}`,
      );
      await onChange();
    } catch (err) {
      setError(
        err instanceof ApiError ? JSON.stringify(err.body) : String(err),
      );
    } finally {
      setBusy(false);
    }
  };

  const [lastDelivery, setLastDelivery] = useState<EmailDelivery | null>(null);

  const resend = async () => {
    setBusy(true);
    setError(null);
    setLastDelivery(null);
    try {
      const res = await api.post<{ emailDelivery: EmailDelivery }>(
        `/api/admin/invites/${invite.id}/resend`,
      );
      setLastDelivery(res.emailDelivery);
      await onChange();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        const body = err.body as { retryAfterSeconds?: number } | null;
        const seconds = body?.retryAfterSeconds ?? 300;
        setError(
          `Cooldown active. Try again in about ${Math.ceil(seconds / 60)} minute(s).`,
        );
      } else {
        setError(
          err instanceof ApiError ? JSON.stringify(err.body) : String(err),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr>
        <td className="font-medium text-ink-800">{invite.invitedEmail}</td>
        <td className="text-xs text-ink-600">{invite.buildingName}</td>
        <td>
          <code className="text-xs">{invite.roleCode}</code>
          {invite.unitNumber && (
            <span className="text-xs text-ink-500 ml-2">
              Unit {invite.unitNumber}
            </span>
          )}
        </td>
        <td className="text-xs text-ink-500">
          {new Date(invite.issuedAt).toLocaleDateString()}
        </td>
        <td className="text-xs text-ink-500">
          {new Date(invite.expiresAt).toLocaleDateString()}
        </td>
        <td>
          <span
            className="badge"
            data-status={
              archived
                ? "review"
                : revoked
                  ? "rejected"
                  : expired
                    ? "review"
                    : "approved"
            }
          >
            {status}
          </span>
        </td>
        <td className="text-right">
          <div className="inline-flex items-center gap-1.5 justify-end">
            {/*
              Resend stays available on archived rows so an admin
              who archived prematurely can rotate + re-send
              without unarchiving first. Disabled while busy so
              consecutive clicks don't fire two requests.
            */}
            <Button
              type="button"
              variant="ghost"
              onClick={() => void resend()}
              loading={busy}
              disabled={busy}
            >
              <RefreshCcw size={14} />
              Resend
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => void archive()}
              disabled={busy}
              title={
                archived
                  ? "Bring this invite back into the active list."
                  : "Hide this invite from the list. Row stays in the audit trail."
              }
            >
              {archived ? (
                <>
                  <ArchiveRestore size={14} /> Unarchive
                </>
              ) : (
                <>
                  <Archive size={14} /> Archive
                </>
              )}
            </Button>
          </div>
        </td>
      </tr>
      {(error || lastDelivery) && (
        <tr>
          <td colSpan={7}>
            {error && <div className="alert-error">{error}</div>}
            {lastDelivery && (
              <div className="text-[12px] text-ink-600 inline-flex items-center gap-2">
                <span>Resent.</span>
                <EmailDeliveryBadge delivery={lastDelivery} />
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Renders the per-send delivery telemetry returned by the issue
// and resend endpoints. Three states map to three badges:
//
//   sent    Green check. The provider accepted the message.
//   stub    Sand chip. RESEND_API_KEY isn't set so the email
//           was logged to the server console instead of sent;
//           share the code out-of-band.
//   failed  Red badge with the upstream error string.
// ---------------------------------------------------------------------------
function EmailDeliveryBadge({ delivery }: { delivery: EmailDelivery }) {
  if (delivery.status === "sent") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-status-approved-bg text-status-approved-fg"
        title={
          delivery.providerId
            ? `Resend message id: ${delivery.providerId}`
            : "Email delivered"
        }
      >
        <Check size={11} />
        Email delivered
      </span>
    );
  }
  if (delivery.status === "stub") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-sand-100 text-ink-600"
        title="RESEND_API_KEY is unset; the email was logged to the server console."
      >
        <Mail size={11} />
        Stubbed (share code by hand)
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-status-rejected-bg text-status-rejected-fg"
      title={delivery.error}
    >
      <AlertTriangle size={11} />
      Email failed
    </span>
  );
}
